'use client'

import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Mail, Lock, Eye, EyeOff } from 'lucide-react'
import { PasswordResetForm } from './password-reset-form'
import type { LoginFormState, LoginFormActions } from '../_hooks/use-login-form'

interface LoginFormProps extends LoginFormState, LoginFormActions {}

export function LoginForm({
  isLoading,
  email,
  password,
  showPassword,
  loginError,
  showResetForm,
  resetEmail,
  resetSent,
  setEmail,
  setPassword,
  setShowPassword,
  setShowResetForm,
  setResetEmail,
  setResetSent,
  setLoginError,
  handleEmailLogin,
  handlePasswordReset,
}: LoginFormProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-gray-50 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-12 animate-fade-in">
          <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
            G-force
          </h1>
          <p className="text-lg text-muted-foreground">業務効率を最大化する</p>
        </div>

        <Card className="p-10 bg-white/90 backdrop-blur-2xl border-0 shadow-2xl rounded-3xl animate-slide-in">
          {showResetForm ? (
            <PasswordResetForm
              isLoading={isLoading}
              resetEmail={resetEmail}
              resetSent={resetSent}
              loginError={loginError}
              setResetEmail={setResetEmail}
              setShowResetForm={setShowResetForm}
              setResetSent={setResetSent}
              setLoginError={setLoginError}
              handlePasswordReset={handlePasswordReset}
            />
          ) : (
            <form onSubmit={handleEmailLogin} className="space-y-4 mb-6">
              <div>
                <label className="text-sm font-medium text-label mb-1 block">
                  メールアドレス
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-subtle" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@g-house.com"
                    autoComplete="email"
                    className="w-full h-12 pl-10 pr-4 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-info focus:border-transparent transition-all"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-label mb-1 block">パスワード</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-subtle" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="パスワードを入力"
                    autoComplete="current-password"
                    className="w-full h-12 pl-10 pr-12 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-info focus:border-transparent transition-all"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-subtle hover:text-body"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {loginError && <p className="text-sm text-red-500 text-center">{loginError}</p>}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-[var(--g-green)] hover:opacity-90 text-white font-medium rounded-xl transition-all duration-300"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    ログイン中...
                  </div>
                ) : (
                  'ログイン'
                )}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setShowResetForm(true)
                  setResetEmail(email)
                  setLoginError('')
                }}
                className="w-full text-sm text-primary hover:text-blue-800 hover:underline mt-3 text-center"
              >
                パスワードを忘れた方はこちら
              </button>
            </form>
          )}
        </Card>
      </div>
    </div>
  )
}
